import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { useChargePointsStore } from '../../store/charge-points-store';
import { useLocationStore } from '@/features/location/store/location-store';
import {
  buildNearbyRows,
  formatDistance,
  formatPriceLabel,
  type NearbyRow,
} from '../../utils/nearby';

const TILE_STYLE: Record<NearbyRow['status'], string> = {
  available: 'bg-ok/10 text-ok dark:bg-ok/15',
  busy: 'bg-warn/10 text-warn dark:bg-warn/15',
  offline: 'bg-ink/5 text-muted',
};

/**
 * Free chargers as plain text - "3/20" over "free" for sites, the count for
 * single chargers - coloured by status (green = some free, orange = all
 * busy, grey = offline). Text scans faster than the ring chart it replaces,
 * and the word "free" says what the fraction counts.
 */
function FreeTile({ row }: { row: NearbyRow }) {
  const { t } = useTranslation();
  const isSite = row.wrapper.type === 'chargePointGroup';
  const label = isSite ? `${row.available}/${row.total}` : `${row.available}`;
  return (
    <div
      className={cn(
        // Fixed width keeps every row's name aligned; long counts shrink
        'flex h-11 w-16 shrink-0 flex-col items-center justify-center rounded-xl tabular-nums',
        TILE_STYLE[row.status]
      )}
      aria-hidden
    >
      <span
        className={cn(
          'whitespace-nowrap font-bold leading-none',
          label.length <= 4
            ? 'text-[15px]'
            : label.length <= 5
              ? 'text-[13px]'
              : 'text-xs tracking-tight'
        )}
      >
        {label}
      </span>
      <span className="mt-0.5 text-[10px] font-medium leading-none">
        {t('nearby.freeWord', { count: row.available })}
      </span>
    </div>
  );
}

/**
 * Charge points in the current viewport, nearest to the map center first.
 * Selecting a row opens it like clicking its pin.
 */
export function NearbyList() {
  const { t, i18n } = useTranslation();
  const chargePoints = useChargePointsStore((s) => s.singleChargePoints);
  const clusterCount = useChargePointsStore(
    (s) => s.clusterChargePoints.length
  );
  const selectedId = useChargePointsStore((s) => s.selectedChargePoint?.id);
  const selectChargePoint = useChargePointsStore((s) => s.selectChargePoint);
  const center = useLocationStore((s) => s.mapCenter);
  const bounds = useLocationStore((s) => s.visibleRegion);

  const rows = useMemo(
    () => buildNearbyRows(chargePoints, center, bounds),
    [chargePoints, center, bounds]
  );

  if (rows.length === 0) {
    return (
      <p className="px-6 py-10 text-center text-sm text-muted">
        {clusterCount > 0 ? t('nearby.zoomIn') : t('nearby.empty')}
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {clusterCount > 0 && (
        <p className="mx-4 mt-3 rounded-xl bg-ink/5 px-3 py-2 text-[13px] text-muted">
          {t('nearby.zoomIn')}
        </p>
      )}
      <div className="flex shrink-0 justify-between px-5 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
        <span>{t('nearby.count', { count: rows.length })}</span>
        <span>{t('nearby.nearestFirst')}</span>
      </div>
      <ul className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {rows.map((row) => {
          const price = formatPriceLabel(row.priceLabel, i18n.language);
          const selected = row.wrapper.id === selectedId;
          return (
            <li key={row.wrapper.id}>
              <button
                type="button"
                onClick={() => selectChargePoint(row.wrapper)}
                aria-current={selected || undefined}
                aria-label={`${row.name}, ${t('nearby.available', { available: row.available, total: row.total })}`}
                className={cn(
                  'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl p-2.5 text-left transition-colors',
                  selected
                    ? 'bg-monta-blue/10 ring-1 ring-monta-blue/30 dark:bg-monta-blue/25 dark:ring-monta-blue/50'
                    : 'hover:bg-ink/5'
                )}
              >
                <FreeTile row={row} />
                <span className="min-w-0">
                  <span className="block truncate text-[15px] font-semibold tracking-tight">
                    {row.name}
                  </span>
                  <span className="block truncate text-[13px] text-muted">
                    {[
                      row.maxKw && `${row.maxKw} kW`,
                      row.plug,
                      formatDistance(row.meters, i18n.language),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="text-right text-[15px] font-semibold tabular-nums">
                  {price?.free
                    ? t('nearby.free')
                    : price
                      ? price.value
                      : t('nearby.priceVaries')}
                  {price && !price.free && (
                    <span className="block text-[11px] font-medium text-muted">
                      {price.unit}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
